import type { Route } from "./+types/privacy";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { detectLocale } from "~/i18n/index";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  return [
    { title: `Privacy Policy — Caratzon` },
    { name: "description", content: "Caratzon Privacy Policy describing how we collect, use, and protect your personal data." },
    ...socialMeta({ title: "Privacy Policy — Caratzon", description: "How Caratzon collects, uses, and protects your data.", pathname: "/privacy", locale }),
  ];
}

const EFFECTIVE_DATE = "March 4, 2026";

const sectionHeadingStyle = {
  fontFamily: "var(--font-display)",
  fontWeight: 300,
  fontSize: "clamp(1.3rem, 2.2vw, 1.7rem)",
  lineHeight: 1.25,
  letterSpacing: "0.01em",
} as const;

const subHeadingStyle = {
  fontFamily: "var(--font-sans)",
  fontWeight: 600,
  fontSize: "0.85rem",
  letterSpacing: "0.02em",
} as const;

const bodyStyle = {
  fontFamily: "var(--font-sans)",
  fontSize: "0.82rem",
  lineHeight: 1.85,
  color: "var(--muted-foreground)",
} as const;

export default function PrivacyPolicyRoute() {
  return (
    <MarketingLayout>
      <div className="px-6 lg:px-12 pt-20 pb-24 max-w-3xl mx-auto">
        {/* Header */}
        <p
          className="issue-label mb-4"
          style={{ fontFamily: "var(--font-sans)", opacity: 0.4 }}
        >
          Legal
        </p>
        <h1
          className="font-display font-light text-foreground mb-3"
          style={{ fontSize: "clamp(2rem, 4vw, 3rem)", lineHeight: 1.1, letterSpacing: "0.01em" }}
        >
          Privacy Policy
        </h1>
        <p style={{ ...bodyStyle, fontStyle: "italic", marginBottom: "3rem" }}>
          Effective date: {EFFECTIVE_DATE}
        </p>

        <div className="space-y-10">

          {/* 1 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>1. Introduction</h2>
            <p style={bodyStyle}>
              Clean Jewel Beyond Limited ("Caratzon," "we," "us"), a company incorporated in Hong Kong operating the Caratzon platform, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal data when you use the Caratzon platform ("Platform"), including the website at caratzon.com, the trade catalog, designer studio, and all related services.
            </p>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              By using the Platform, you consent to the practices described in this policy. If you do not agree, please discontinue use of the Platform.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>2. Data Controller</h2>
            <p style={bodyStyle}>
              The data controller responsible for your personal data is:
            </p>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              Clean Jewel Beyond Limited<br />
              Hong Kong<br />
              <a href="mailto:privacy@caratzon.com" className="underline hover:text-foreground transition-colors">privacy@caratzon.com</a>
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>3. Information We Collect</h2>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>3.1 Information You Provide</h3>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li><strong>Account data</strong> — name, email address, password, role (Designer, B2B Buyer, Manufacturer, Production Manager), business name, and business address</li>
              <li><strong>Profile data</strong> — designer biography, portfolio images, brand description, and professional credentials</li>
              <li><strong>Design assets</strong> — CAD files, reference images, design descriptions, and metadata uploaded by designers</li>
              <li><strong>Transaction data</strong> — order details, customization briefs, licensing selections, proposed fees, and negotiation communications</li>
              <li><strong>Payment data</strong> — billing information processed through Stripe Connect (we do not store full card numbers on our servers)</li>
              <li><strong>Communications</strong> — messages exchanged through the Platform's messaging system, support requests, and feedback</li>
            </ul>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>3.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li><strong>Device and browser data</strong> — IP address, browser type, operating system, device identifiers, and screen resolution</li>
              <li><strong>Usage data</strong> — pages visited, features used, search queries, designs viewed, time spent on pages, and interaction patterns</li>
              <li><strong>Analytics data</strong> — design view counts, save events, scroll depth, and engagement metrics used to improve the Platform</li>
              <li><strong>Cookies and similar technologies</strong> — session cookies, authentication tokens, language preferences, and optional analytics cookies (see our Cookie Policy)</li>
            </ul>

            <h3 className="text-foreground mt-4 mb-2" style={subHeadingStyle}>3.3 Information from Third Parties</h3>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li><strong>Payment processors</strong> — transaction confirmations and payout status from Stripe</li>
              <li><strong>Authentication providers</strong> — identity verification data if you sign in through a third-party provider</li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>4. How We Use Your Information</h2>
            <p style={bodyStyle}>We use your personal data for the following purposes:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li><strong>Platform operations</strong> — to create and manage your account, authenticate sessions, and provide access to role-specific features</li>
              <li><strong>Design presentation</strong> — to display designs in the catalog, generate AI-enhanced stories and lookbook imagery, and create editorial content</li>
              <li><strong>Transaction facilitation</strong> — to process orders, manage licensing workflows, route production to manufacturers, and handle payments</li>
              <li><strong>Communication</strong> — to send order updates, system notifications, and respond to support requests</li>
              <li><strong>Analytics and improvement</strong> — to understand usage patterns, improve Platform features, and optimize the user experience</li>
              <li><strong>Security</strong> — to detect and prevent fraud, unauthorized access, and other security threats</li>
              <li><strong>Legal compliance</strong> — to comply with applicable laws, regulations, and legal processes</li>
            </ul>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>5. AI Processing</h2>
            <p style={bodyStyle}>
              The Platform uses artificial intelligence services (currently Google Gemini) to process design assets for the following purposes:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li>Generating design stories and editorial narratives from uploaded images and metadata</li>
              <li>Creating lookbook and catalog imagery for design presentation</li>
              <li>Enhancing catalog and portrait photographs</li>
              <li>Analyzing jewelry designs and CAD renders for manufacturing specifications</li>
              <li>Translating messages between platform users</li>
            </ul>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              Design assets sent to AI services are processed solely for the purposes described above. We do not use your designs to train third-party AI models. AI-generated content derived from your designs is used exclusively within the Platform.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>6. How We Share Your Information</h2>
            <p style={bodyStyle}>We may share your personal data with the following categories of recipients:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li><strong>Other platform users</strong> — designer profiles and design information are visible to buyers; buyer identity is shared with designers and manufacturers as part of order workflows</li>
              <li><strong>Manufacturing partners</strong> — production specifications, CAD files (under NDA), and order details are shared with vetted manufacturers assigned to fulfill orders</li>
              <li><strong>Payment processors</strong> — Stripe receives payment and billing information necessary to process transactions</li>
              <li><strong>Cloud infrastructure providers</strong> — data is stored and processed using cloud services (including AWS S3 for asset storage and PostgreSQL for application data)</li>
              <li><strong>AI service providers</strong> — design assets are processed by Google's Gemini AI services as described in Section 5</li>
              <li><strong>Legal and regulatory authorities</strong> — when required by law, regulation, or legal process</li>
            </ul>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              We do not sell your personal data to third parties. We do not share your data with advertisers.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>7. Data Security</h2>
            <p style={bodyStyle}>
              We implement appropriate technical and organizational measures to protect your personal data, including:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li>Encryption of data in transit (TLS/HTTPS) and at rest</li>
              <li>Role-based access controls limiting data visibility by user type</li>
              <li>Time-limited, signed URLs for design asset access — preventing unauthorized redistribution</li>
              <li>Production CAD files accessible only to vetted manufacturers under NDA</li>
              <li>Regular security reviews and monitoring</li>
            </ul>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              While we take reasonable precautions, no system is completely secure. We cannot guarantee absolute security of your data.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>8. Data Retention</h2>
            <p style={bodyStyle}>
              We retain your personal data for as long as your account is active or as needed to provide services. Specific retention periods:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li><strong>Account data</strong> — retained for the duration of your account plus 12 months after deletion</li>
              <li><strong>Design assets</strong> — retained while the design is listed on the Platform; upon removal, assets are deleted within 90 days unless subject to an active license agreement</li>
              <li><strong>Transaction records</strong> — retained for 7 years to comply with financial record-keeping requirements</li>
              <li><strong>Analytics data</strong> — aggregated and anonymized within 24 months</li>
              <li><strong>Audit logs</strong> — retained for 3 years for security and compliance purposes</li>
            </ul>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>9. Your Rights</h2>
            <p style={bodyStyle}>
              Depending on your jurisdiction, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1" style={bodyStyle}>
              <li><strong>Access</strong> — request a copy of the personal data we hold about you</li>
              <li><strong>Rectification</strong> — request correction of inaccurate or incomplete data</li>
              <li><strong>Erasure</strong> — request deletion of your personal data, subject to legal retention requirements</li>
              <li><strong>Restriction</strong> — request that we limit processing of your data in certain circumstances</li>
              <li><strong>Portability</strong> — request your data in a structured, machine-readable format</li>
              <li><strong>Objection</strong> — object to processing based on legitimate interests</li>
              <li><strong>Withdraw consent</strong> — where processing is based on consent, you may withdraw it at any time</li>
            </ul>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              To exercise any of these rights, contact us at{" "}
              <a href="mailto:privacy@caratzon.com" className="underline hover:text-foreground transition-colors">privacy@caratzon.com</a>.
              We will respond within 30 days.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>10. International Data Transfers</h2>
            <p style={bodyStyle}>
              Caratzon operates from Hong Kong with offices in Guangdong and London. Your data may be transferred to and processed in jurisdictions outside your country of residence, including Hong Kong, China, the United Kingdom, and the United States (for cloud infrastructure and AI processing).
            </p>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              Where data is transferred internationally, we ensure appropriate safeguards are in place, including standard contractual clauses and data processing agreements with our service providers.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>11. Cookies</h2>
            <p style={bodyStyle}>
              The Platform uses cookies and similar technologies for authentication, language preferences, and analytics. For detailed information about the cookies we use and how to manage them, please see our{" "}
              <a href="/cookies" className="underline hover:text-foreground transition-colors">Cookie Policy</a>.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>12. Children's Privacy</h2>
            <p style={bodyStyle}>
              The Platform is not intended for individuals under the age of 18. We do not knowingly collect personal data from children. If you believe we have inadvertently collected data from a minor, please contact us immediately at{" "}
              <a href="mailto:privacy@caratzon.com" className="underline hover:text-foreground transition-colors">privacy@caratzon.com</a>.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>13. Changes to This Policy</h2>
            <p style={bodyStyle}>
              We may update this Privacy Policy from time to time. Material changes will be communicated via email or a prominent notice on the Platform. The "Effective date" at the top of this page indicates when the policy was last revised. Continued use of the Platform after changes constitutes acceptance.
            </p>
          </section>

          {/* 14 */}
          <section>
            <h2 className="text-foreground mb-3" style={sectionHeadingStyle}>14. Contact</h2>
            <p style={bodyStyle}>
              For questions or concerns about this Privacy Policy or our data practices, contact us at:
            </p>
            <p style={{ ...bodyStyle, marginTop: "0.75rem" }}>
              Clean Jewel Beyond Limited<br />
              Data Protection Enquiries<br />
              <a href="mailto:privacy@caratzon.com" className="underline hover:text-foreground transition-colors">privacy@caratzon.com</a>
            </p>
          </section>
        </div>
      </div>
    </MarketingLayout>
  );
}
